import React from 'react'
import classNames from 'classnames/bind'

import useFieldfolioPrices from 'hooks/useFieldfolioPrices'
import Icon from 'components/Icon'

import styles from './MarketingPricingPanel.module.css'

const cx = classNames.bind(styles)

type MarketingPricingPanelProps = {
  className?: string
}

const Cell = ({
  children,
  isTinted = false,
  isKey = false,
  icon,
  qualifier
}: {
  children?: React.ReactNode
  isTinted?: boolean
  isKey?: boolean
  icon?: 'check' | 'circle'
  qualifier?: string
}) => (
  <div className={cx(styles.cell, { isTinted, isKey })}>
    {children && (<>
      <div className={styles.cellText}>{children}</div>
      {qualifier && <div className={styles.addonPrice}>{qualifier}</div>}
      </>)}
    {icon === 'check' && <Icon kind="check-circle" size={16} color="var(--colorGreen8)" />}
    {icon === 'circle' && <Icon kind="circle" size={16} color="var(--colorGray4)" />}
  </div>
)

type Tier = {
  price: number | null
  includedCatalogs: number
  includedReps: number
  includedStaff: number
}

const MarketingPricingPanel = ({ className }: MarketingPricingPanelProps) => {
  const { getRecurringPlanPricing, getIncludedAddonsCount, getRecurringPlanFeature } = useFieldfolioPrices({})

  // Startup
  const startupTier: Tier = {
    price: getRecurringPlanPricing({ tier: 'STARTUP', component: 'base' }),
    includedCatalogs: getIncludedAddonsCount({ tier: 'STARTUP', component: 'catalogs' }),
    includedReps: getIncludedAddonsCount({ tier: 'STARTUP', component: 'reps' }),
    includedStaff: getIncludedAddonsCount({ tier: 'STARTUP', component: 'staff' })
  }

  // Business
  const businessTier: Tier = {
    price: getRecurringPlanPricing({ component: 'base' }),
    includedCatalogs: getIncludedAddonsCount({ component: 'catalogs' }),
    includedReps: getIncludedAddonsCount({ component: 'reps' }),
    includedStaff: getIncludedAddonsCount({ component: 'staff' })
  }
  // Business
  const enterpriseTier: Tier = {
    price: getRecurringPlanPricing({ tier: 'ENTERPRISE', component: 'base' }),
    includedCatalogs: getIncludedAddonsCount({ tier: 'ENTERPRISE', component: 'catalogs' }),
    includedReps: getIncludedAddonsCount({ tier: 'ENTERPRISE', component: 'reps' }),
    includedStaff: getIncludedAddonsCount({ tier: 'ENTERPRISE', component: 'staff' })
  }

  return (
    <>
      <div className={cx(styles.container, className)}>
        <div className={cx(styles.column, styles.orderCountColumn)}>
          <Cell isTinted isKey qualifier="Add more for $59/mo">
            Catalogs
          </Cell>
          <Cell isKey qualifier="Add more for $29/mo">Sales Reps</Cell>
          <Cell isTinted isKey qualifier="Add more for $29/mo">
            Admin Seats
          </Cell>
          <Cell isKey>Unlimited Products</Cell>
          <Cell isTinted isKey>
            Unlimited Customers
          </Cell>
          <Cell isKey>iPad App</Cell>
          <Cell isTinted isKey>
            B2B Marketplace
          </Cell>
          <Cell isKey>Integrations</Cell>
          <Cell isTinted isKey>
            API
          </Cell>
        </div>

        <div className={styles.column}>
          <div className={cx(styles.cell, styles.headerCell)}>Startup</div>
          <div className={cx(styles.cell, styles.priceCell)}>
            <div className={cx(styles.priceNumber)}>${startupTier.price}</div>
            <div className={styles.priceDescriptor}>Billed Monthly</div>
          </div>

          <Cell isTinted>{startupTier.includedCatalogs}</Cell>
          <Cell>{startupTier.includedReps}</Cell>
          <Cell isTinted>{startupTier.includedStaff}</Cell>

          <Cell icon="check" />
          <Cell isTinted icon="check" />
          <Cell icon="check" />
          <Cell isTinted icon="check" />
          <Cell
            icon={getRecurringPlanFeature({ tier: 'STARTUP', feature: 'accounting-integrations' }) ? 'check' : 'circle'}
          />
          <Cell
            isTinted
            icon={getRecurringPlanFeature({ tier: 'STARTUP', feature: 'api-access' }) ? 'check' : 'circle'}
          />
        </div>

        <div className={cx(styles.column, styles.mostPopular)}>
          <div className={cx(styles.cell, styles.headerCell)}>Business</div>
          <div className={cx(styles.cell, styles.priceCell)}>
            <div className={cx(styles.priceNumber, styles.fieldfolioPrice)}>${businessTier.price}</div>
            <div className={styles.priceDescriptor}>Billed Monthly</div>
          </div>

          <Cell isTinted>{businessTier.includedCatalogs}</Cell>
          <Cell>{businessTier.includedReps}</Cell>
          <Cell isTinted>{businessTier.includedStaff}</Cell>

          <Cell icon="check" />
          <Cell isTinted icon="check" />
          <Cell icon="check" />
          <Cell isTinted icon="check" />
          <Cell
            icon={
              getRecurringPlanFeature({ tier: 'BUSINESS', feature: 'accounting-integrations' }) ? 'check' : 'circle'
            }
          />
          <Cell
            isTinted
            icon={getRecurringPlanFeature({ tier: 'BUSINESS', feature: 'api-access' }) ? 'check' : 'circle'}
          />
        </div>

        <div className={styles.column}>
          <div className={cx(styles.cell, styles.headerCell)}>Enterprise</div>
          <div className={cx(styles.cell, styles.priceCell)}>
            <div className={cx(styles.priceNumber)}>${enterpriseTier.price}</div>
            <div className={styles.priceDescriptor}>Billed Monthly</div>
          </div>

          <Cell isTinted>{enterpriseTier.includedCatalogs}</Cell>
          <Cell>{enterpriseTier.includedReps}</Cell>
          <Cell isTinted>{enterpriseTier.includedStaff}</Cell>

          <Cell icon="check" />
          <Cell isTinted icon="check" />
          <Cell icon="check" />
          <Cell isTinted icon="check" />
          <Cell
            icon={
              getRecurringPlanFeature({ tier: 'ENTERPRISE', feature: 'accounting-integrations' }) ? 'check' : 'circle'
            }
          />
          <Cell
            isTinted
            icon={getRecurringPlanFeature({ tier: 'ENTERPRISE', feature: 'api-access' }) ? 'check' : 'circle'}
          />
        </div>
      </div>
      <div className={styles.qualifiers}>Pricing shown in Australian Dollars (AUD) excluding GST.</div>
    </>
  )
}

export default MarketingPricingPanel
